/**
 * Operations · Inventory Movement.
 */
import { useMemo, useState } from "react";
import { MoveRight, Truck, Undo2, PackageMinus, Boxes, ArrowDownToLine, Wrench } from "lucide-react";
import { Card, SectionHeader, TextInput, Button } from "../../components/lib";
import { TabBar, ListRow, Drawer, Field } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { useDeepLink } from "../../lib/integration";
import { MOVEMENTS, MovementKind, Movement as MovementT } from "../../mocks/operationsData";

const KIND_META: Record<MovementKind, { icon: any; label: string; tint: string; fg: string }> = {
  receipt:     { icon: ArrowDownToLine, label: "Receipt",     tint: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  transfer:    { icon: MoveRight,       label: "Transfer",    tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  adjustment:  { icon: Wrench,          label: "Adjustment",  tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  return:      { icon: Undo2,           label: "Return",      tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
  consumption: { icon: PackageMinus,    label: "Consumption", tint: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)"     },
};

export default function Movement() {
  const { q: initialQ } = useDeepLink();
  const [tab, setTab] = useState<MovementKind | "all">("all");
  const [q, setQ]     = useState(initialQ);
  const [selected, setSelected] = useState<MovementT | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: MOVEMENTS.length };
    MOVEMENTS.forEach((m) => { c[m.kind] = (c[m.kind] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => MOVEMENTS.filter((m) =>
      (tab === "all" || m.kind === tab) &&
      (!q || m.sku.toLowerCase().includes(q.toLowerCase()) || m.name.toLowerCase().includes(q.toLowerCase()) || m.reference.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, q],
  );

  return (
    <div className="space-y-6" data-testid="op-movement">
      <Card testId="op-mov-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search SKU, product, or PO ref…" testId="op-mov-search" /></div>
        </div>
        <div className="mt-4">
          <TabBar<MovementKind | "all">
            testId="op-mov-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",         label: "All",         count: counts.all         },
              { key: "receipt",     label: "Receipts",    count: counts.receipt     || 0 },
              { key: "transfer",    label: "Transfers",   count: counts.transfer    || 0 },
              { key: "consumption", label: "Consumption", count: counts.consumption || 0 },
              { key: "return",      label: "Returns",     count: counts.return      || 0 },
              { key: "adjustment",  label: "Adjustments", count: counts.adjustment  || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="op-mov-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Ledger" title={`${filtered.length} movement${filtered.length === 1 ? "" : "s"}`} action={<Boxes size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        </div>
        <div className="p-2" data-testid="op-mov-list">
          {filtered.map((m) => {
            const meta = KIND_META[m.kind];
            const Icon = meta.icon;
            const positive = m.qty >= 0;
            return (
              <ListRow
                key={m.id}
                testId={`op-mov-${m.id}`}
                onClick={() => setSelected(m)}
                icon={
                  <span className="inline-flex items-center justify-center" style={{
                    width: 28, height: 28, borderRadius: "999px",
                    background: meta.tint, color: meta.fg,
                  }}>
                    <Icon size={13} />
                  </span>
                }
                label={
                  <span className="inline-flex items-center gap-2">
                    <span className="font-mono text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>{m.sku}</span>
                    <span>{m.name}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: meta.tint, color: meta.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {meta.label}
                    </span>
                  </span>
                }
                sublabel={
                  <>
                    <span className="inline-flex items-center gap-1">
                      {m.from} <Truck size={10} className="inline" /> {m.to}
                    </span>
                    <span> · {m.operator} · {m.timestamp} · <span className="font-mono">{m.reference}</span></span>
                  </>
                }
                trailing={
                  <div className="text-right">
                    <div className="text-sm font-semibold tabular-nums" style={{ color: positive ? "var(--ph-success)" : "var(--ph-danger)" }}>
                      {positive ? "+" : ""}{m.qty}
                    </div>
                  </div>
                }
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Stock Movement 360" testId="op-mov-drawer" width={520}>
        {selected && (() => {
          const meta = KIND_META[selected.kind];
          const positive = selected.qty >= 0;
          return (
            <div>
              <div className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{selected.reference}</div>
              <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.25rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{selected.name}</div>
              <Field label="SKU"          inline><span className="text-sm font-mono">{selected.sku}</span></Field>
              <Field label="Kind"         inline>
                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: meta.tint, color: meta.fg, borderRadius: "var(--ph-radius-sm)" }}>
                  {meta.label}
                </span>
              </Field>
              <Field label="Quantity"     inline><span className="text-sm font-semibold tabular-nums" style={{ color: positive ? "var(--ph-success)" : "var(--ph-danger)" }}>{positive ? "+" : ""}{selected.qty}</span></Field>
              <Field label="Source"       inline><span className="text-sm">{selected.from}</span></Field>
              <Field label="Destination"  inline><span className="text-sm">{selected.to}</span></Field>
              <Field label="Reference"    inline><span className="text-sm font-mono">{selected.reference}</span></Field>
              <Field label="Operator"     inline><span className="text-sm">{selected.operator}</span></Field>
              <Field label="Timestamp"    inline><span className="text-sm">{selected.timestamp}</span></Field>
              <Field label="Reason"       inline><span className="text-sm capitalize">{selected.kind}</span></Field>
              <Field label="Approval"     inline><span className="text-sm">System · auto-posted</span></Field>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Audit trail</div>
                <AuditTrail collection="movements" recordId={selected.id} testId="op-mov-audit" />
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <Button variant="secondary" testId="op-mov-reverse">Reverse movement</Button>
              </div>
            </div>
          );
        })()}
      </Drawer>
    </div>
  );
}
